import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { formatRatio } from '../../lib/utils';
import { useInsightsWithAccounts, type InsightWithAccount } from '../../hooks/useAggregatedInsights';
import { useDateRangeStore } from '../../store/dateRangeStore';
import { useAccountStore } from '../../store/accountStore';
import {
  getSetting,
  PERFORMERS_MIN_SPEND_KEY,
  PERFORMERS_MIN_SPEND_DEFAULT,
} from '../../api/settings';

interface PerformerItemProps {
  insight: InsightWithAccount;
  rank: number;
  isTop: boolean;
}

function PerformerItem({ insight, rank, isTop }: PerformerItemProps) {
  const roasColor = isTop
    ? insight.roas && insight.roas >= 3
      ? 'text-green-600'
      : insight.roas && insight.roas >= 1
      ? 'text-yellow-600'
      : 'text-red-600'
    : 'text-red-600';

  return (
    <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          isTop ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}
      >
        {rank}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900" title={insight.name}>
          {insight.name}
        </p>
        <p className="truncate text-xs text-gray-500" title={insight.accountName}>
          {insight.accountName}
        </p>
      </div>
      <div className={`flex-shrink-0 text-sm font-semibold ${roasColor}`}>
        {insight.roas !== null ? formatRatio(insight.roas) : '-'}
      </div>
    </div>
  );
}

export default function TopBottomPerformers() {
  const { t } = useTranslation();
  const { dateRange } = useDateRangeStore();
  const { selectedAccountId } = useAccountStore();

  const { data: insights, isLoading } = useInsightsWithAccounts({
    selectedAccountId,
    dateRange: 'custom',
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // Configurable minimum spend threshold. Falls back to default while loading.
  const { data: minSpend } = useQuery({
    queryKey: ['setting', PERFORMERS_MIN_SPEND_KEY],
    queryFn: () => getSetting<number>(PERFORMERS_MIN_SPEND_KEY),
    staleTime: 1000 * 60 * 5,
  });
  const effectiveMinSpend =
    typeof minSpend === 'number' && Number.isFinite(minSpend) && minSpend >= 0
      ? minSpend
      : PERFORMERS_MIN_SPEND_DEFAULT;

  const { topPerformers, bottomPerformers } = useMemo(() => {
    if (!insights || insights.length === 0) {
      return { topPerformers: [], bottomPerformers: [] };
    }

    // Filter insights that have ROAS data, positive ROAS, and spend ≥ threshold
    const withRoas = insights.filter(
      (i) => i.roas !== null && i.roas > 0 && i.spend >= effectiveMinSpend
    );
    const sorted = [...withRoas].sort((a, b) => (b.roas || 0) - (a.roas || 0));

    // Get top 3 and bottom 3
    const top = sorted.slice(0, 3);
    const bottom = sorted.slice(-3).reverse(); // Reverse to show lowest first

    return { topPerformers: top, bottomPerformers: bottom };
  }, [insights, effectiveMinSpend]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border bg-white p-6 shadow-sm">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const hasData = topPerformers.length > 0 || bottomPerformers.length > 0;

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      {/* Top Performers */}
      <div className="border-b p-4">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-green-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('performers.topPerformers')}
          </h3>
        </div>
        {hasData ? (
          <div className="space-y-2">
            {topPerformers.map((insight, index) => (
              <PerformerItem
                key={insight.id}
                insight={insight}
                rank={index + 1}
                isTop={true}
              />
            ))}
            {topPerformers.length === 0 && (
              <p className="text-center text-sm text-gray-400">{t('common.noData')}</p>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-gray-400">{t('common.noData')}</p>
        )}
      </div>

      {/* Bottom Performers */}
      <div className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('performers.bottomPerformers')}
          </h3>
        </div>
        {hasData ? (
          <div className="space-y-2">
            {bottomPerformers.map((insight, index) => (
              <PerformerItem
                key={insight.id}
                insight={insight}
                rank={index + 1}
                isTop={false}
              />
            ))}
            {bottomPerformers.length === 0 && (
              <p className="text-center text-sm text-gray-400">{t('common.noData')}</p>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-gray-400">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
}
